import { Component, HostBinding, ViewChild } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, HelpIndex, UpdateStatusType } from '../../../../models/enums';
import { ExportDb } from '../../../../models/export-database';
import { CellTemplateType, GenericColDef } from '../../../../models/grid-context';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { NewExportDatabaseModalComponent } from '../../new-export-database-modal/new-export-database-modal.component';
import { ObjectActionListType, ActionType, Permissions } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-export-databases',
    templateUrl: './export-databases.component.html',
})
export class ExportDatabasesComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) exportDbGrid: GenericGridComponent;

    gridName = 'ExportDb';
    isDirty: boolean;
    validChanges = true;
    helpIndex = HelpIndex.org_export_db_manage_dlg;
    automationId = 'export-databases';
    updateStatusType = UpdateStatusType.EXPORT_DATABASE;

    // permissions
    hasDBViewOnlyPermissions: boolean;
    hasDBCreatePermissions: boolean;
    hasDBDeletePermissions: boolean;

    constructor(private commonService: CommonService) {
    }

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Name',
            field: 'name',
            automationId: this.automationId,
            lockVisible: true,
            width: 350,
            suppressSizeToFit: true
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId
        },
        {
            headerName: 'IP',
            field: 'ipAddress',
            automationId: this.automationId,
            width: 150,
            suppressSizeToFit: true
        },
        {
            headerName: 'Port',
            field: 'port',
            automationId: this.automationId,
            width: 100,
            suppressSizeToFit: true
        },
        {
            headerName: 'Database Name',
            field: 'databaseName',
            automationId: this.automationId,
            width: 350,
            suppressSizeToFit: true
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );

    rowClassRules = {
        'disabled-row': () => {
            return this.hasDBViewOnlyPermissions;
        }
    };

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.orgManager_listExportDatabaseConnections,
        responseName: 'connections',
        data: {}
    });

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: this.automationId,
            click: (item) => { this.newDatabase(item); },
            permissions: [{ permissionType: ObjectActionListType.EXPORT_DB_CONNECTION, permissionActions: [ActionType.CREATE] }]
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: this.automationId,
            click: (item) => { this.deleteDb(item); },
            permissions: [{ permissionType: ObjectActionListType.EXPORT_DB_CONNECTION, permissionActions: [ActionType.DELETE] }]
        })
    ];

    responseArrayCallback = (value: any) => new ExportDb(value);

    cellDoubleClickedCallback = (rowNode: RowNode) => {
        if (rowNode && rowNode.data) {
            this.commonService.openModal(NewExportDatabaseModalComponent, { modalData: rowNode.data, class: 'horizontal-px-500' }).then((result) => {
                if (result.modalResult) {
                    this.exportDbGrid.loadData();
                }
            });
        }
    }
    saveChanges(): Promise<boolean> { return Promise.resolve(true); }

    deleteDb(item: ExportDb): void {
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: item });
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string): void {
        this.exportDbGrid.loadData();
        this.hasDBCreatePermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.EXPORT_DB_CONNECTION,
            permissionActions: [ActionType.CREATE]
        }]);
        this.hasDBViewOnlyPermissions = Permissions.checkUserPermission(ObjectActionListType.EXPORT_DB_CONNECTION) && !this.hasDBCreatePermissions;
        this.hasDBDeletePermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.EXPORT_DB_CONNECTION,
            permissionActions: [ActionType.DELETE]
        }]);

        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (this.hasDBViewOnlyPermissions) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
            }
        }
    }

    newDatabase(editDb?: ExportDb): void {
        this.commonService.openModal(NewExportDatabaseModalComponent, { modalData: editDb, class: 'horizontal-px-500' }).then((result) => {
            if (result.modalResult) {
                this.exportDbGrid.loadData();
            }
        });
    }
}
